import { TDjangoModelData } from '../responses/django-models-data-response';
import { TPopupQueryParams } from '@/components/InformationModal/types';
import { TFeatureInfoData } from '../responses/feature-info-response';
import { TQueryParams } from '../query-params';
import {
  TDefaultLayerConfig,
  TDefaultLayerConfigAttribute
} from '../responses/default-layer-config';
import { TFeature, TMultiPolygon, TProperties } from '../map-geometries';
import { TDesktopLayerConfig } from '../responses/desktop-layer-config';

export const enum EPopupTags {
  LAYER_CONFIG = 'layerConfig',
  FEATURE_INFO_DATA = 'featureInfoData',
  FEATURE_INFO_JOINS = 'featureInfoJoins',
  COVER_PATH = 'coverPath',
  ATTACHMENTS = 'attachments',
  DJANGO_MODELS_LAYERS = 'djangoModelLayers',
  DJANGO_MODELS_DATA = 'djangoModelData',
  CONTENT_TYPE_INFO = 'contentTypeInfo',
  FEATURE_INFO_LAYERS = 'featureInfoLayers',
  ALL_FEATURE_INFO_LAYERS = 'allFeatureInfoLayers',
  WMS_LAYER_INFO = 'wmsLayerInfo',
  SHARE_PLOTS_IDENTIFICATION = 'sharePlotsIdentification',
  JOINED_LAYERS_ATTRIBUTES = 'joinedLayerAttributes'
}

export type TFetchAttachmentsParams = {
  isDjangoModel?: boolean;
  district: string;
  mapPortalId: number;
  layerId: number;
  objectId: string | number;
};

export type TFetchFeatureInfoLayersQueryParams = {
  lat?: number;
  lng?: number;
  parcel?: string;
  bbox?: string;
  width?: number;
  height?: number;
  refresh?: number;
  layers?: string[];
} & Partial<TPopupQueryParams>;

export type TFetchFeatureInfoLayersParams = {
  district: string;
  compositionId: number;
  params: TFetchFeatureInfoLayersQueryParams;
  visibleLayers?: boolean;
};

export type TFetchFeatureInfoDataParams = {
  district: string;
  projectId: number;
  mapPortalCompositionId: number;
  data: TQueryParams;
  errorMessage?: string;
};

export type TFetchFeatureInfoJoinsParams = {
  district: string;
  mapPortalCompositionId: number;
  layerConfigId: number;
};

export type TFetchCoverPath = Omit<TFetchAttachmentsParams, 'isWms'> & {
  district: string;
  mapPortalId: number;
  layerId: number;
  objectId: string | number;
  coverId: string | number;
  isDjangoModel?: boolean;
};

export type TTransformedFeatureInfoDataResponse = {
  count: number;
  results: TFeatureInfoData[];
};

export type TFetchDjangoModelsDataParams = {
  district: string;
  objectId: number;
  params: {
    composition_id: number;
    lat?: number;
    lng?: number;
    parcel?: string;
    page?: number;
    page_size?: number;
  };
};

export type TTransformedDjangoModelsDataResponse = {
  count: number;
  results: TDjangoModelData[];
};

export type TExtendedDefaultLogicAttribute = TDefaultLayerConfigAttribute & {
  name: string;
};

export type TParsedDefaultLayerConfig = Omit<TDefaultLayerConfig, 'attrs'> & {
  attributes: TExtendedDefaultLogicAttribute[];
};

export type TConfigsResponse<ConfigType> = {
  configs: ConfigType[];
  timestamp: number;
};

export type TFetchDjangoModelsLayersResponse = TConfigsResponse<
  TParsedDefaultLayerConfig
>;

export type TFetchFeatureInfoLayersResponse = TConfigsResponse<
  TDesktopLayerConfig
>;

export type TFetchContentTypeInfoParams = {
  data: { composition_id: number; content_type_ids: number[] };
};

export type TFeatureInfoLayersQuery = {
  district: string;
  compositionId: number;
};

export type TWmsLayerDataParams = {
  SERVICE: string;
  REQUEST: string;
  VERSION: string;
  layers: string;
  CRS: string;
  BBOX: string;
  width: number;
  height: number;
  INFO_FORMAT: string;
  I: number;
  J: number;
  QUERY_LAYERS: string;
  RADIUS: number;
  BUFFER: number;
};

export type TFetchWmsLayerDataParams = {
  url: string;
  params: TWmsLayerDataParams;
  errorMessage: string;
};

export type TParsedXmlResponse = {
  [key: string]: string | number | null | TParsedXmlResponse;
};

type TCrsProperties = {
  code: string;
};

type TCrs = {
  type: string;
  properties: TCrsProperties;
};

export type TWmsLayerJsonResponse = {
  type: string;
  features: TFeature<TMultiPolygon>[];
  crs: TCrs;
};

export type TParsedWmsLayerJsonResponse = {
  properties: TProperties;
  geometry: TMultiPolygon;
};

export type TSharePlotsIdentification = {
  parcels: string[];
};

export type TGetSharedPlotsIdentification = Record<'id' | 'district', string>;

type TJoinedLayerAttributesParamsKeys =
  | 'SERVICE'
  | 'VERSION'
  | 'REQUEST'
  | 'TYPENAME'
  | 'OUTPUTFORMAT'
  | 'EXP_FILTER';

export type TJoinedLayerAttributesRequestParams = Record<
  TJoinedLayerAttributesParamsKeys,
  string
>;

export type TJoinedLayerAttributesParams = {
  project: number;
  params: TJoinedLayerAttributesRequestParams;
};

export type TWmsLayerResponse =
  | TParsedWmsLayerJsonResponse
  | TParsedXmlResponse
  | null;
